import { useState } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Scale, Shield, BookOpen, Users, Smartphone } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Auth({ onLogin }) {
  const [loginData, setLoginData] = useState({ email: "", password: "", mfa_code: "" });
  const [registerData, setRegisterData] = useState({
    email: "",
    password: "",
    full_name: "",
    job_title: "",
    role: "avocat"
  });
  const [loading, setLoading] = useState(false);
  // P0.2 — MFA step state
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [pendingLoginData, setPendingLoginData] = useState(null);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = mfaRequired
        ? { ...pendingLoginData, mfa_code: mfaCode }
        : { email: loginData.email, password: loginData.password };

      const response = await axios.post(`${API}/auth/login`, payload, { withCredentials: true });
      toast.success("Connexion réussie");
      onLogin(response.data.access_token, response.data.user, response.data.cabinet_name);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 202 && detail === "MFA_REQUIRED") {
        // Password OK — MFA step needed
        setPendingLoginData({ email: loginData.email, password: loginData.password });
        setMfaRequired(true);
        toast.info("Veuillez entrer votre code MFA");
      } else {
        toast.error(detail || "Erreur de connexion");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, registerData, { withCredentials: true });
      toast.success("Compte créé avec succès");
      onLogin(response.data.access_token, response.data.user, response.data.cabinet_name);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'inscription");
    } finally {
      setLoading(false);
    }
  };

  const roleLabels = {
    admin: "Administrateur",
    avocat: "Avocat",
    juriste: "Juriste",
    relecteur: "Relecteur",
    stagiaire: "Stagiaire"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-8 items-center">
        {/* Left side - Branding */}
        <div className="hidden md:block space-y-8">
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className="bg-blue-600 p-3 rounded-xl">
                <Scale className="h-8 w-8 text-white" />
              </div>
              <h1 className="text-5xl font-bold text-blue-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                Lawliet.ai
              </h1>
            </div>
            <p className="text-xl text-slate-600" style={{ fontFamily: 'Inter, sans-serif' }}>
              L'intelligence juridique pour les professionnels du droit
            </p>
          </div>

          <div className="space-y-6">
            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Shield className="h-6 w-6 text-blue-900" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">100% Européen</h3>
                <p className="text-sm text-slate-600">Données hébergées en Europe, conformité RGPD garantie</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Users className="h-6 w-6 text-blue-900" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Gestion multi-cabinets</h3>
                <p className="text-sm text-slate-600">Contrôle d'accès par rôle, séparation stricte des données</p>
              </div>
            </div>

            <div className="flex items-start space-x-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <BookOpen className="h-6 w-6 text-blue-900" />
              </div>
              <div>
                <h3 className="font-semibold text-blue-900 mb-1">Assistant juridique IA</h3>
                <p className="text-sm text-slate-600">Analyse de documents, recherche juridique, citations vérifiables</p>
              </div>
            </div>
          </div>
        </div>

        {/* Right side - Auth forms */}
        <Card className="shadow-2xl border-0">
          <CardHeader>
            <div className="md:hidden flex items-center justify-center mb-4">
              <Scale className="h-8 w-8 text-blue-900" />
            </div>
            <CardTitle className="text-2xl text-center">Bienvenue</CardTitle>
            <CardDescription className="text-center">
              Connectez-vous ou créez votre cabinet
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login" className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="login" data-testid="login-tab">Connexion</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Inscription</TabsTrigger>
              </TabsList>

              <TabsContent value="login">
                {!mfaRequired ? (
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="login-email">Email</Label>
                    <Input
                      id="login-email"
                      data-testid="login-email-input"
                      type="email"
                      placeholder="avocat@cabinet.fr"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="login-password">Mot de passe</Label>
                    <Input
                      id="login-password"
                      data-testid="login-password-input"
                      type="password"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                    data-testid="login-submit-button"
                  >
                    {loading ? "Connexion..." : "Se connecter"}
                  </Button>
                </form>
                ) : (
                /* P0.2 — MFA verification step */
                <form onSubmit={handleLogin} className="space-y-5">
                  <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                    <Smartphone className="h-5 w-5 text-blue-700 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-semibold text-blue-900">Vérification MFA requise</p>
                      <p className="text-xs text-blue-700 mt-0.5">Ouvrez votre application d'authentification (Google Authenticator, Authy…) et entrez le code à 6 chiffres.</p>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Code MFA</Label>
                    <Input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]{6}"
                      placeholder="000000"
                      maxLength={6}
                      value={mfaCode}
                      onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="text-center text-2xl tracking-[0.5em] font-mono"
                      autoFocus
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading || mfaCode.length !== 6}>
                    {loading ? "Vérification..." : "Valider le code"}
                  </Button>
                  <Button type="button" variant="ghost" className="w-full text-sm" onClick={() => { setMfaRequired(false); setMfaCode(""); }}>
                    ← Retour
                  </Button>
                </form>
                )}
              </TabsContent>

              <TabsContent value="register">
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="register-name">Nom complet</Label>
                    <Input
                      id="register-name"
                      data-testid="register-name-input"
                      type="text"
                      placeholder="Jean Dupont"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-email">Email professionnel</Label>
                    <Input
                      id="register-email"
                      data-testid="register-email-input"
                      type="email"
                      placeholder="avocat@cabinet.fr"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-job">Fonction</Label>
                    <Input
                      id="register-job"
                      data-testid="register-job-input"
                      type="text"
                      placeholder="Avocat spécialisé en droit des affaires"
                      value={registerData.job_title}
                      onChange={(e) => setRegisterData({ ...registerData, job_title: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="register-password">Mot de passe</Label>
                    <Input
                      id="register-password"
                      data-testid="register-password-input"
                      type="password"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700"
                    disabled={loading}
                    data-testid="register-submit-button"
                  >
                    {loading ? "Création..." : "Créer mon cabinet"}
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-2">
                    En vous inscrivant, vous devenez administrateur de votre cabinet
                  </p>
                </form>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
